import dayjs from "dayjs";
import * as yup from "yup";
import {
  getValueOrBlank,
  getValueOrZero,
  isNotEmpty,
} from "../../helpers/utils";
import { ASC, RowValue } from "./MuiTable";

export const hasMultipleNestedArrays = (row: any, isGroupView?: boolean) => {
  if (isGroupView) {
    for (const key in row) {
      if (Array.isArray(row[key]) && row[key].length > 1) {
        return true;
      }
    }
  }

  return false;
};

const compareValues = (
  valueA: any,
  valueB: any,
  sortDirection: string,
  considerSortAsString: boolean = false,
): number => {
  const isNumeric = !isNaN(Number(valueA)) || !isNaN(Number(valueB));
  const isString = typeof valueA === "string" || typeof valueB === "string";

  if (isNumeric && !considerSortAsString) {
    return sortDirection === ASC
      ? Number(getValueOrZero(valueA)) - Number(getValueOrZero(valueB))
      : Number(getValueOrZero(valueB)) - Number(getValueOrZero(valueA));
  }

  if (isString) {
    return sortDirection === ASC
      ? getValueOrBlank(valueA).localeCompare(getValueOrBlank(valueB))
      : getValueOrBlank(valueB).localeCompare(getValueOrBlank(valueA));
  }

  if (valueA < valueB) return sortDirection === ASC ? -1 : 1;
  if (valueA > valueB) return sortDirection === ASC ? 1 : -1;

  return 0;
};

// Sort the filtered results based on the selected column and direction
/**
 * Sorts an array of data based on a specified column and direction, with support for nested objects,
 * empty values, and price fields with currency formatting.
 * @param sortKeyName - the key name in nested object within record, on which we want to perform sorting.
 * @param arrayData - The array of data to be sorted. Each element is a record with string, number, or ReactNode values.
 * @param column - The column key to sort the data by.
 * @param direction - The sorting direction, either "ASC" for ascending or "DESC" for descending.
 * @param objectKeyName - (Optional) The key name for nested objects within the data records.
 * @param isEmptyValue - (Optional) A flag indicating if the records for the specified field could be empty.
 *                        If true, records with empty values are moved to the end.
 *                              If true, the function parses and sorts the numeric values.
 * @returns A new array of sorted data.
 */
const extractNestedValues = (
  obj: any,
  column: string,
  objectKeyName?: string,
  sortKeyName?: string | null,
  considerSortAsString: boolean = false,
) => {
  if (obj && objectKeyName && isNotEmpty(obj[objectKeyName])) {
    const nestedArray = obj[objectKeyName];
    if (Array.isArray(nestedArray)) {
      const keyToSortBy = sortKeyName ?? column;
      nestedArray.sort((a, b) =>
        compareValues(a?.[keyToSortBy], b?.[keyToSortBy], ASC, considerSortAsString),
      );
      return nestedArray[0]?.[keyToSortBy];
    }
  }
  return obj[column];
};

const handleEmptyValues = (
  aValue: any,
  bValue: any,
  direction: string,
): number => {
  const isAEmpty =
    aValue === null ||
    aValue === undefined ||
    (Array.isArray(aValue) && aValue.length === 0) ||
    aValue === "";

  const isBEmpty =
    bValue === null ||
    bValue === undefined ||
    (Array.isArray(bValue) && bValue.length === 0) ||
    bValue === "";

  // If both are empty
  if (isAEmpty && isBEmpty) return 0;

  // If only A is empty, push it to end
  if (isAEmpty) return 1;

  // If only B is empty, push it to end
  if (isBEmpty) return -1;

  // Check if both are valid ISO dates
  if (dayjs(aValue).isValid() && dayjs(bValue).isValid()) {
    return direction === ASC
      ? dayjs(aValue).diff(dayjs(bValue))
      : dayjs(bValue).diff(dayjs(aValue));
  }

  // Otherwise fallback to string comparison
  return direction === ASC
    ? String(aValue).localeCompare(String(bValue))
    : String(bValue).localeCompare(String(aValue));
};

export const sortTableData = (
  arrayData: Record<string, RowValue>[],
  column: string | null,
  direction: string,
  objectKeyName?: string,
  isEmptyValue?: boolean,
  sortKeyName?: string | null,
  considerSortAsString: boolean = false,
) => {
  if (!(column && direction)) return arrayData;

  return [...arrayData].sort((a, b) => {
    const aValue = extractNestedValues(a, column, objectKeyName, sortKeyName);
    const bValue = extractNestedValues(b, column, objectKeyName, sortKeyName);

    if (isEmptyValue) {
      return handleEmptyValues(aValue, bValue, direction);
    }

    return compareValues(aValue, bValue, direction, considerSortAsString);
  });
};

export const getSchema = (
  validPageNumberMessage: string,
  fetchValidationMessage: (key: string) => string,
  commonSiteCoreKeys: { pageNumberRequired: string },
  priorityResults: Record<string, RowValue>[],
  tableResults: Record<string, RowValue>[],
  rowsPerPage: number,
) =>
  yup.object().shape({
    tablePaginationPageNo: yup
      .number()
      .typeError(validPageNumberMessage)
      .positive(validPageNumberMessage)
      .integer(validPageNumberMessage)
      .required(fetchValidationMessage(commonSiteCoreKeys.pageNumberRequired))
      .test("is-valid", validPageNumberMessage, (value) => {
        const totalPages = Math.ceil(
          [...priorityResults, ...tableResults].length / rowsPerPage,
        );

        if (value <= 0 || value > totalPages) {
          return false;
        }

        return true;
      }),
  });
